import {
  Button,
  Card,
  CardFooter,
  mergeClasses,
  Tooltip,
} from "@fluentui/react-components";
import {
  CopyRegular,
  EyeRegular,
  Info16Regular,
  OpenRegular,
  ShareRegular,
  Star20Filled,
  Star20Regular,
} from "@fluentui/react-icons";
import { ConstantValues } from "common/constants";
import { IAccessLLMModel } from "models/IAccessLLMModel";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { useSharedAdminCellStyles } from "theme/sharedStyles";
import { copyTextToClipboard, getModelByKey } from "utils/generalUtils";
import { showToast } from "../../../redux/toastActions";
import {
  useSharedCardStyles,
  useSharedCloseButtonStyles,
  useSharedTagStyles,
  useSharedTextStyles,
} from "../../../theme/sharedStyles";
import { useStyles } from "./useCasePromptCardCompact.styles";
import { IUseCasePromptCardCompactProps } from "./useCasePromptCardCompact.types";

const TAG_VARIANTS = [
  "tagSelling",
  "tagSupplyChain",
  "tagBusinessImpact",
] as const;
type TagVariantKey = (typeof TAG_VARIANTS)[number];

function getUseCaseTagVariant(title: string): TagVariantKey | undefined {
  if (!title?.trim()) return undefined;
  const hash = title
    .split(ConstantValues.EMPTY_STRING)
    .reduce((acc, ch) => (acc * 31 + ch.charCodeAt(0)) & 0x7fffffff, 0);
  if (hash % 4 === 0) {
    return undefined;
  }

  return TAG_VARIANTS[hash % TAG_VARIANTS.length];
}

const CARD_LABELS = {
  favourite: "Favourite",
  unfavourite: "Unfavourite",
  addToFavorites: "Add to favorites",
  removeFromFavorites: "Remove from favorites",
  qualsEnabled: "Qualifications",
  proposalsEnabled: "Proposals",
  citationsEnabled: "Citations",
  useThisPrompt: "Use this prompt in current chat",
  copyPromptText: "Copy prompt to clipboard",
  viewThisPrompt: "View Prompt Text",
  share: "Share link to prompt",
};

export const UseCasePromptCardCompact = (
  props: IUseCasePromptCardCompactProps,
) => {
  const styles = useStyles();
  const adminCellStyles = useSharedAdminCellStyles();
  const cardStyles = useSharedCardStyles();
  const closeButtonStyles = useSharedCloseButtonStyles();
  const tagStyles = useSharedTagStyles();
  const textStyles = useSharedTextStyles();
  const dispatch = useDispatch();
  const llmModels: IAccessLLMModel[] = useSelector(
    (state: RootState) => state.accessLLMModelsSlice.value,
  );

  const handleCopyPromptText = async () => {
    try {
      const tempElement = document.createElement("div");
      tempElement.innerHTML = props.prompt.prompt;
      const plainText =
        tempElement.textContent ||
        tempElement.innerText ||
        ConstantValues.EMPTY_STRING;

      copyTextToClipboard(plainText);
      dispatch(showToast("Prompt copied to clipboard", "success"));
    } catch (err) {
      console.error("Failed to copy prompt text:", err);
      dispatch(showToast("Failed to copy prompt", "error"));
    }
  };

  const tagVariant = getUseCaseTagVariant(props.useCase.title);

  const modelDisplayName = props.prompt.llmModel
    ? (getModelByKey(llmModels, props.prompt.llmModel)?.displayName ??
      props.prompt.llmModel)
    : null;

  const hasFeatureChips =
    props.prompt.qualsEnabled ||
    props.prompt.proposalsEnabled ||
    props.prompt.citationsEnabled ||
    (props.prompt.preselectedKnowledgeComponents?.length ?? 0) > 0 ||
    !!props.prompt.llmModel;

  return (
    <Card
      className={mergeClasses(cardStyles.card, styles.cardHover)}
      key={props.prompt.id}
    >
      <Button
        className={mergeClasses(
          closeButtonStyles.closeButton,
          styles.starButton,
          props.isFavorite && styles.starButtonFilled,
        )}
        shape="circular"
        appearance="subtle"
        size="small"
        aria-label={
          props.isFavorite ? CARD_LABELS.unfavourite : CARD_LABELS.favourite
        }
        aria-pressed={props.isFavorite}
        title={
          props.isFavorite
            ? CARD_LABELS.removeFromFavorites
            : CARD_LABELS.addToFavorites
        }
        icon={props.isFavorite ? <Star20Filled /> : <Star20Regular />}
        onClick={() => props.onToggleFavorite(props.prompt)}
      />

      <div className={styles.container}>
        <div
          className={mergeClasses(
            styles.tag,
            tagVariant === "tagSupplyChain" && tagStyles.tagWarning,
            tagVariant === "tagBusinessImpact" && tagStyles.tagError,
            tagVariant === "tagSelling" && styles.tagSelling,
          )}
        >
          {props.useCase.title}
        </div>

        <div className={styles.titleRow}>
          <span
            className={adminCellStyles.truncatedText}
            title={props.prompt.title}
          >
            {props.prompt.title}
          </span>
          {props.prompt.description && (
            <Tooltip content={props.prompt.description} relationship="label">
              <Info16Regular className={styles.infoIcon} />
            </Tooltip>
          )}
        </div>

        {hasFeatureChips && (
          <div className={styles.chips}>
            {modelDisplayName && (
              <Tooltip content={modelDisplayName} relationship="label">
                <span className={styles.chipLlm}>LLM</span>
              </Tooltip>
            )}
            {props.prompt.qualsEnabled && (
              <span className={styles.chipFeature}>
                {CARD_LABELS.qualsEnabled}
              </span>
            )}
            {props.prompt.proposalsEnabled && (
              <span className={styles.chipFeature}>
                {CARD_LABELS.proposalsEnabled}
              </span>
            )}
            {props.prompt.citationsEnabled && (
              <span className={styles.chipFeature}>
                {CARD_LABELS.citationsEnabled}
              </span>
            )}
            {props.prompt.preselectedKnowledgeComponents &&
              props.prompt.preselectedKnowledgeComponents.length > 0 && (
                <Tooltip
                  content={`${props.prompt.preselectedKnowledgeComponents.length} Knowledge Component${props.prompt.preselectedKnowledgeComponents.length > 1 ? "s" : ConstantValues.EMPTY_STRING} Preselected`}
                  relationship="label"
                >
                  <span className={styles.chipFeature}>
                    {props.prompt.preselectedKnowledgeComponents.length} KC
                  </span>
                </Tooltip>
              )}
          </div>
        )}

        {props.prompt.owner?.displayName && (
          <div className={textStyles.caption}>
            Owner: {props.prompt.owner.displayName}
          </div>
        )}

        <CardFooter className={styles.footer}>
          <div className={styles.footerLeftButtons}>
            <Tooltip content={CARD_LABELS.viewThisPrompt} relationship="label">
              <Button
                size="small"
                appearance="subtle"
                icon={<EyeRegular />}
                className={mergeClasses(
                  closeButtonStyles.closeButton,
                  styles.iconButtonSize,
                )}
                onClick={() => props.onViewPrompt(props.prompt)}
              />
            </Tooltip>

            <Tooltip content={CARD_LABELS.copyPromptText} relationship="label">
              <Button
                size="small"
                appearance="subtle"
                icon={<CopyRegular />}
                className={mergeClasses(
                  closeButtonStyles.closeButton,
                  styles.iconButtonSize,
                )}
                onClick={handleCopyPromptText}
              />
            </Tooltip>

            <Tooltip content={CARD_LABELS.share} relationship="label">
              <Button
                size="small"
                appearance="subtle"
                icon={<ShareRegular />}
                className={mergeClasses(
                  closeButtonStyles.closeButton,
                  styles.iconButtonSize,
                )}
                onClick={() =>
                  props.onCopyToClipboard(props.prompt, props.useCase.id)
                }
              />
            </Tooltip>
          </div>

          <Tooltip
            content={CARD_LABELS.useThisPrompt}
            relationship="description"
          >
            <Button
              size="small"
              appearance="subtle"
              icon={<OpenRegular />}
              aria-label={CARD_LABELS.useThisPrompt}
              className={mergeClasses(
                closeButtonStyles.closeButton,
                styles.iconButtonSize,
              )}
              onClick={() => props.onUsePrompt(props.prompt, props.useCase)}
            />
          </Tooltip>
        </CardFooter>
      </div>
    </Card>
  );
};
